<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { debouncer } from '@/Util/utils';
  import type { MyFitnessStores } from '../stores';
  import { cn } from '@/lib/utils';
  import { Button } from '@/lib/components/ui/button';
  import * as Dialog from '$lib/components/ui/dialog';
  import { dateInTimezone, ProgressionFilterFields, todayInTimezone } from './ProgressionFilterFields';
  import ProgressionSearchFilter from './ProgressionSearchFilter.svelte';
  import PeriodStatsPanel from './PeriodStats/PeriodStats.svelte';
  import type { ActivityMarker, Breakthrough, RaceEvent } from './chartData';
  import ProgressionCharts from './ProgressionCharts.svelte';
  import {
    DEFAULT_MEASURES_URL,
    DEFAULT_STATS_URL,
    fetchMeasures,
    fetchPeriodStats,
    type MeasureRow,
    type PeriodStats,
  } from './progressionApi';

  /** The My Fitness stores, where there are any (not on the coach and squad pages): they say when trickle-down ends. */
  export let myFitness: MyFitnessStores | null = null;

  /**
   * How far down the sticky filter sidebar has to start, i.e. the bottom edge of the site header and
   * the my-fitness tab bar that are already stuck to the top of the page.
   */
  export let stickyTop: string = '46px';

  /** The athlete's IANA timezone: the date presets are "today" there, and the server reads dates in it. */
  export let timezone: string = '';
  /** Where the period stats come from. A coach or squad view passes the athlete's own URL. */
  export let statsUrl: string = DEFAULT_STATS_URL;
  /** Where the daily fitness history comes from (also the source of the charts). */
  export let measuresUrl: string = DEFAULT_MEASURES_URL;

  let filterDialogOpen = false; // mobile only

  // The desktop sidebar collapses to a rail, as on the Activities tab. Remembered across visits.
  const COLLAPSE_KEY = 'progressionFiltersCollapsed';
  let filtersCollapsed = readCollapsed();
  function readCollapsed(): boolean {
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch (error) {
      return false;
    }
  }
  function toggleFilters() {
    filtersCollapsed = !filtersCollapsed;
    try {
      window.localStorage.setItem(COLLAPSE_KEY, filtersCollapsed ? '1' : '0');
    } catch (error) {
      // Private browsing; the preference just doesn't outlive the page.
    }
  }

  let stats: PeriodStats | null = null;
  let statsLoading = true;
  let statsFailed = false;
  let statsRequest: AbortController | null = null;

  // Only the newest request may land: a slow response for an earlier filter would otherwise
  // overwrite a newer one.
  async function loadStats(silent = false) {
    statsRequest?.abort();
    const request = (statsRequest = new AbortController());
    // A silent reload (after a background job) keeps what is on screen until the new numbers arrive.
    if (!silent) statsLoading = true;
    statsFailed = false;
    try {
      const result = await fetchPeriodStats(fields, statsUrl, request.signal);
      if (request !== statsRequest) return;
      stats = result;
    } catch (error) {
      if (request.signal.aborted) return;
      statsFailed = true;
    }
    if (request === statsRequest) statsLoading = false;
  }

  // The daily fitness history depends only on where the range starts, not on the activity filters,
  // so it is fetched when that changes rather than on every filter click.
  let measures: MeasureRow[] | null = null;
  let breakthroughs: Breakthrough[] | null = null;
  let markers: ActivityMarker[] | null = null;
  let raceEvents: RaceEvent[] | null = null;
  let measuresFailed = false;
  let measuresStart: number | null = null;
  let measuresRequest: AbortController | null = null;
  $: if (stats && stats.start_ts !== measuresStart) loadMeasures(stats.start_ts);
  async function loadMeasures(periodStart: number) {
    measuresStart = periodStart;
    measuresRequest?.abort();
    const request = (measuresRequest = new AbortController());
    try {
      const result = await fetchMeasures(periodStart, measuresUrl, request.signal);
      if (request !== measuresRequest) return;
      measures = result.history ?? [];
      breakthroughs = result.ba ?? [];
      markers = result.markers ?? [];
      raceEvents = result.race_events ?? [];
      measuresFailed = false;
    } catch (error) {
      // The stats still stand; only the figures taken from the history (focus at the range ends) are missing.
      if (!request.signal.aborted && request === measuresRequest) {
        measures = null;
        measuresFailed = true;
      }
    }
  }

  // The date range that has actually been applied. The date inputs change `fields` as they are typed
  // into, before there is a valid range; the charts should only ever follow what the stats followed.
  let appliedFrom = '';
  let appliedTo = '';
  const today = todayInTimezone(timezone);

  let fields: ProgressionFilterFields = new ProgressionFilterFields(
    () => {
      appliedFrom = fields.dateFrom;
      appliedTo = fields.dateTo;
      loadStats();
    },
    () => (fields = fields),
    timezone
  );
  appliedFrom = fields.dateFrom;
  appliedTo = fields.dateTo;
  onMount(() => loadStats());
  onDestroy(() => {
    statsRequest?.abort();
    measuresRequest?.abort();
  });

  // Uploads, edits, deletes and above all trickle-down rewrite the fitness history and the stats, so reload
  // both when the server says one has finished (as the Today and Activities tabs do). A burst of events
  // becomes a single refetch.
  const liveEvents = [
    'upload_activity_complete',
    'modify_activity_complete',
    'delete_activity_complete',
    'schedule_activity_complete',
    'trickle_complete',
  ];
  const refreshDebouncer = debouncer(400);
  async function refresh() {
    await loadStats(true);
    if (stats) loadMeasures(stats.start_ts);
  }
  const onLiveEvent = () => refreshDebouncer.debounce(refresh);

  onMount(() => {
    // @ts-ignore
    const channel = window.Xert?._userChannel;
    if (!channel) return;
    for (const event of liveEvents) channel.bind(event, onLiveEvent);
    return () => {
      for (const event of liveEvents) channel.unbind(event, onLiveEvent);
      refreshDebouncer.clear();
    };
  });

  // The MyFitness page also tracks trickle-down in a store; refresh when it goes from running to done.
  const userParams = myFitness?.userParams;
  const trickleActive = myFitness?.trickleActive;
  let wasTrickling = false;
  const stopWatchingTrickle = trickleActive?.subscribe((trickling) => {
    if (wasTrickling && !trickling) onLiveEvent();
    wasTrickling = trickling;
  });
  onDestroy(() => stopWatchingTrickle?.());

  // Where a Forecast AI plan with a chosen start date begins (or will begin, during Pre-Plan Maintenance), as
  // `YYYY-MM-DD` in the athlete's profile timezone (matching `today` above); '' when there is none.
  let programStart = '';
  const stopWatchingTrainingPlan = myFitness?.trainingPlan?.subscribe((plan) => {
    const firstDay: number | undefined = plan?.hasForecast ? plan.forecastResults?.days?.[0]?.t : undefined;
    programStart = firstDay ? dateInTimezone(new Date(firstDay * 1000), timezone) : '';
  });
  onDestroy(() => stopWatchingTrainingPlan?.());

  // Dragging across a chart picks the date range (the same as choosing it in the sidebar).
  function selectRange(from: string, to: string) {
    fields.setCustomRange(from, to);
    fields = fields;
  }

  function resetFilters() {
    fields.resetFilters();
    fields = fields;
  }
</script>

<progression class="xert-svelte h-full flex flex-row gap-4 border-solid border-0 border-t border-border">
  <div
    class={cn(
      'hidden lg:flex flex-col sticky h-full shrink-0 border-solid border-0 border-r border-border bg-muted/30 overflow-x-hidden',
      filtersCollapsed ? 'w-[42px] px-1 py-2' : 'w-[320px] py-4'
    )}
    style="top: {stickyTop}; max-height: calc(100dvh - {stickyTop} - 1rem); {filtersCollapsed
      ? `min-height: calc(100dvh - ${stickyTop} - 1rem);`
      : ''}"
  >
    <div class={cn('flex flex-row items-center gap-2 px-4', filtersCollapsed ? 'justify-center' : 'justify-between mb-2')}>
      {#if !filtersCollapsed}
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Filters</h3>
      {/if}
      <Button
        variant="ghost"
        size="icon"
        class="text-muted-foreground"
        title={filtersCollapsed ? 'Show filters' : 'Hide filters'}
        on:click={toggleFilters}
      >
        <i class={cn('fa-solid relative', filtersCollapsed ? 'fa-sliders' : 'fa-chevron-left')} aria-hidden="true">
          {#if filtersCollapsed && (fields.isFilterApplied() || fields.isDateRangeApplied())}
            <div class="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
          {/if}
        </i>
      </Button>
    </div>
    {#if !filtersCollapsed}
      <div class="flex-1 min-h-0 px-4 overflow-y-auto overflow-x-hidden">
        <ProgressionSearchFilter bind:fields />
      </div>
      <div class="shrink-0 pt-3 mt-1 px-4 border-solid border-0 border-t border-border">
        <Button variant="outline" class="w-full" on:click={resetFilters}>Reset</Button>
      </div>
    {/if}
  </div>

  <div class="h-full flex-1 min-w-0 flex flex-col gap-4 overflow-x-clip py-4 pl-2 pr-2 md:pl-4 md:pr-4 lg:pl-0">
    <div class="lg:hidden">
      <Button variant="outline" class="w-full space-x-1" on:click={() => (filterDialogOpen = true)}>
        <span>Filters</span>
        <i class="fa-light fa-sliders-up relative" aria-hidden="true">
          {#if fields.isFilterApplied() || fields.isDateRangeApplied()}
            <div class="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" />
          {/if}
        </i>
      </Button>
    </div>

    <PeriodStatsPanel {stats} loading={statsLoading} failed={statsFailed} {measures} {timezone} />

    <section class={cn('rounded-lg border border-solid border-border bg-white p-4 min-h-[400px]')}>
      <h2 class="text-base font-medium m-0 mb-2">Progression Charts</h2>
      <ProgressionCharts rows={measures} irParams={$userParams?.ir_params} {breakthroughs} {raceEvents} {markers} {measuresFailed} from={appliedFrom} to={appliedTo} {today} {programStart} matchedDays={stats?.filtered ? (stats.matched_days ?? null) : null} on:select={(event) => selectRange(event.detail.from, event.detail.to)} />
    </section>
  </div>
</progression>

<!-- Filters dialog for mobile view -->
<Dialog.Root bind:open={filterDialogOpen} portal="#shadcn-ui-dialog-target">
  <Dialog.Content class="flex flex-col max-h-[95dvh] md:h-[600px] max-w-[420px] w-[95vw] overflow-hidden rounded-xl px-3">
    <Dialog.Header class="px-3">
      <Dialog.Title>Filters</Dialog.Title>
    </Dialog.Header>
    <div class="overflow-y-auto px-3">
      <ProgressionSearchFilter bind:fields />
    </div>
    <Dialog.Footer class="gap-2 flex-row justify-end px-3">
      <Button variant="outline" on:click={resetFilters}>Reset</Button>
      <Button variant="outline" on:click={() => (filterDialogOpen = false)}>Apply</Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
